import apiClient from '../client'
import type { PageResponse } from '../types'
import type {
  BankInfoValidationRequest,
  BankInfoValidationResponse,
  PaymentJobResponse,
  PaymentJobRecipientResponse,
  PaymentJobCreateRequest,
  PaymentJobTransferExportRequest,
  PaymentJobUpdateRequest,
  PaymentAmountAdjustmentRequest,
  PaymentAdjustmentAttachmentInfo,
} from './types'

export const paymentJobsApi = {
  list(params?: {
    id?: number
    jobNo?: string
    jobName?: string
    registeredBy?: string
    status?: string
    ownerUserId?: number
    excludeDeleted?: boolean
    transferDateFrom?: string
    transferDateTo?: string
    excludeStatuses?: string
    sortBy?: string
    order?: 'ASC' | 'DESC'
    page?: number
    size?: number
  }) {
    return apiClient.get<PageResponse<PaymentJobResponse>>('/api/payment-jobs', { params })
  },

  getById(id: number) {
    return apiClient.get<PaymentJobResponse>(`/api/payment-jobs/${id}`)
  },

  create(data: PaymentJobCreateRequest) {
    return apiClient.post<PaymentJobResponse>('/api/payment-jobs', data)
  },

  update(id: number, data: PaymentJobUpdateRequest) {
    return apiClient.put<PaymentJobResponse>(`/api/payment-jobs/${id}`, data)
  },

  delete(id: number) {
    return apiClient.delete<{ message: string }>(`/api/payment-jobs/${id}`)
  },

  getRecipients(id: number) {
    return apiClient.get<PaymentJobRecipientResponse[]>(`/api/payment-jobs/${id}/recipients`)
  },

  adjustAmount(jobId: number, lineId: number, data: PaymentAmountAdjustmentRequest) {
    return apiClient.put<PaymentJobRecipientResponse>(
      `/api/payment-jobs/${jobId}/recipients/${lineId}/amount-adjustment`,
      data,
      { _silent: true },
    )
  },

  uploadPaymentAdjustmentAttachments(jobId: number, lineId: number, files: File[]) {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    return apiClient.post<void>(
      `/api/payment-jobs/${jobId}/recipients/${lineId}/adjustment-attachments`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
        _silent: true,
      },
    )
  },

  listPaymentAdjustmentAttachments(jobId: number, lineId: number) {
    return apiClient.get<PaymentAdjustmentAttachmentInfo[]>(
      `/api/payment-jobs/${jobId}/recipients/${lineId}/adjustment-attachments/files`,
      { _silent: true },
    )
  },

  deletePaymentAdjustmentAttachment(jobId: number, lineId: number, filename: string) {
    return apiClient.delete<void>(
      `/api/payment-jobs/${jobId}/recipients/${lineId}/adjustment-attachments/files`,
      { params: { filename }, _silent: true },
    )
  },

  downloadPaymentAdjustmentAttachments(jobId: number, lineId: number) {
    return apiClient.get<Blob>(
      `/api/payment-jobs/${jobId}/recipients/${lineId}/adjustment-attachments`,
      {
        responseType: 'blob',
        timeout: 120_000,
        _silent: true,
      },
    )
  },

  exportPayees(id: number) {
    return apiClient.get<Blob>(`/api/payment-jobs/${id}/export-payees`, {
      responseType: 'blob',
      _silent: true,
    })
  },

  /**
   * 選択した複数リストの支給者を、リストごとの CSV（支給者_<jobNo>.csv）を 1 つの ZIP に束ねて取得する。
   * ダウンロードは 1 回（1 ファイル＝支給者一覧_*.zip）で完結する。
   */
  exportPayeesBulk(jobIds: number[]) {
    // エラー時は呼び出し側が blob 本文から取り出したメッセージを Modal で表示するため、
    // インターセプタの自動表示は抑止する（二重表示防止）。
    return apiClient.post<Blob>(
      '/api/payment-jobs/export-payees',
      { jobIds },
      { responseType: 'blob', timeout: 600_000, _silent: true },
    )
  },

  /**
   * 振込データ（全銀協 DAT）を取得する。返るのは 振込データ_*.dat 単体（octet-stream）。
   * 支給者一覧 ZIP は呼び出し側が別途 exportPayeesBulk で取得する（② = dat + zip の 2 ファイル）。
   */
  exportTransfer(data: PaymentJobTransferExportRequest) {
    // エラー時は呼び出し側が blob 本文から取り出したメッセージを Modal で表示するため、
    // インターセプタの自動表示は抑止する（二重表示防止）。
    return apiClient.post<Blob>('/api/payment-jobs/export-transfer', data, {
      responseType: 'blob',
      timeout: 600_000,
      _silent: true,
    })
  },

  /** 受給者口座情報（FY2603〜FY2607）の事前チェック。issues が空なら OK。 */
  validateBankInfo(data: BankInfoValidationRequest) {
    return apiClient.post<BankInfoValidationResponse>(
      '/api/payment-jobs/validate-bank-info',
      data,
      { _silent: true },
    )
  },

  printNotices(jobIds: number[]) {
    return apiClient.post<Blob>(
      '/api/payment-jobs/print-notices',
      { jobIds },
      { responseType: 'blob', timeout: 600_000 },
    )
  },
}
